import { getGrowthBook } from '@/plugins/useGrowthbook'

/**
 * Catalogue of custom events sent to GrowthBook's Managed Warehouse.
 *
 * Each key is the event name as it appears in GrowthBook (use it when
 * defining metrics), and the value is the shape of its properties. Add new
 * events here so names and properties stay consistent across components.
 *
 * Events fire for all users who have given statistics consent — not only those
 * in an active experiment. GrowthBook joins the events table with
 * experiment_views server-side when computing metric results, so only members
 * of each experiment influence its analysis. Sending events for everyone is
 * intentional: it gives GrowthBook a complete population to work from.
 */
export type GrowthBookEvents = {
  'Subscription Purchase': {
    transactionId: string
    value: number
    currency: string
    planId: string
    billingPeriod: string
    customerType: string
  }
  'Game Purchase': {
    transactionId: number
    value: number
    currency: string
    gameCode: string
    gameType?: string
    quantity: number
    customerType: string
  }
  'Lead Submit': {
    campaignCode: string
    prizeCode?: string
  }
}

export type GrowthBookEventName = keyof GrowthBookEvents

/**
 * Send a custom event to GrowthBook. Never throws — tracking failures (or
 * GrowthBook not being initialised) must not affect the user's flow.
 */
export async function trackGrowthBookEvent<K extends GrowthBookEventName>(
  name: K,
  properties: GrowthBookEvents[K]
): Promise<void> {
  try {
    await getGrowthBook().logEvent(name, properties)
  } catch (error) {
    if (import.meta.env.GLP_APP_ENV !== 'production') {
      console.warn(`[GrowthBook] failed to track "${name}"`, error)
    }
  }
}
